import type { DatasourceDto } from "./generated/api-client";

/**
 * Idempotent Sheets datasource create for one wizard run.
 *
 * Always POST. Do not scan GET /datasources for a matching spreadsheet id —
 * that list includes teammate-shared (viewerRole MEMBER) rows, the isDemo
 * sample, and own-project sources under another Google account. Reusing any
 * of those would analyse/generate on the wrong source and skip the server's
 * 409 `sheets_datasource_exists` path.
 *
 * The server returns the existing row for the same project + sheet + Google
 * connection, and 409s with `datasourceId` when the sheet is already
 * connected under another Google account. Concurrent identical POSTs within
 * one wizard run share one in-flight promise.
 */

/** In-flight creates keyed by spreadsheet id + create-body connection. */
const inflight = new Map<string, Promise<string>>();

/** Create body may include googleConnectionId; GET responses never do. */
export type SheetsCreateBody = DatasourceDto & {
  googleConnectionId?: string;
  /**
   * MOSA-668. The tab titles ticked on the review screen. Only these become
   * collections. Omitted means every tab, which is what the wizard sent before.
   */
  sheetsTabs?: string[];
};

type DatasourcesApi = {
  createDatasource: (body: DatasourceDto) => Promise<{ data: { id?: string } }>;
};

export class SheetsAlreadyConnectedError extends Error {
  readonly existingId: string;

  constructor(existingId: string) {
    super("This sheet is already connected with another Google account");
    this.name = "SheetsAlreadyConnectedError";
    this.existingId = existingId;
  }
}

function inflightKey(
  spreadsheetId: string,
  googleConnectionId?: string | null,
): string {
  return `${spreadsheetId}\0${googleConnectionId ?? ""}`;
}

/** Test hook: drop in-flight promises between specs. */
export function __resetSheetsDatasourceInflight(): void {
  inflight.clear();
}

function httpStatus(error: unknown): number | undefined {
  const status = (error as { response?: { status?: number } })?.response?.status;
  return typeof status === "number" ? status : undefined;
}

export function existingIdFromConflict(error: unknown): string | null {
  const data = (error as { response?: { data?: Record<string, unknown> } })
    ?.response?.data;
  if (!data || typeof data !== "object") return null;
  // API (onlydash-api#62): 409 body code is `sheets_datasource_exists`.
  // Real responses put the id in `datasourceId` (not always `id`).
  const code = data.code;
  if (
    typeof code === "string" &&
    code.length > 0 &&
    code !== "sheets_datasource_exists"
  ) {
    return null;
  }
  const id = data.datasourceId ?? data.id ?? data.existingId;
  return typeof id === "string" && id.trim() ? id.trim() : null;
}

/**
 * Create a Sheets datasource (or receive the server's existing id).
 * Concurrent callers for the same spreadsheet+connection share one POST.
 */
export async function findOrCreateSheetsDatasource(
  api: DatasourcesApi,
  args: {
    spreadsheetId: string;
    body: SheetsCreateBody;
  },
): Promise<string> {
  const { spreadsheetId, body } = args;
  if (!spreadsheetId) {
    throw new Error("no spreadsheet id");
  }

  const key = inflightKey(spreadsheetId, body.googleConnectionId);
  const existing = inflight.get(key);
  if (existing) {
    return existing;
  }

  const run = (async () => {
    try {
      const created = await api.createDatasource(body as DatasourceDto);
      const id = created.data?.id;
      if (!id) {
        throw new Error("no id");
      }
      return id;
    } catch (error) {
      if (httpStatus(error) === 409) {
        const existingId = existingIdFromConflict(error);
        if (existingId) {
          throw new SheetsAlreadyConnectedError(existingId);
        }
      }
      throw error;
    }
  })();

  inflight.set(key, run);
  try {
    return await run;
  } finally {
    inflight.delete(key);
  }
}
